import React, {useState, useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {RecyclingChart} from "@/pages/Recycle/recycling-chart";
import TreesEquivalent from './TreesEquivalent';
import RecyclabilityScore from './RecyclabilityScore';
import WasteDiverted from './WasteDiverted';
import {WasteImpactCard} from './WasteImpact.jsx';
import CO2SavingsChart from '../CO2savings/co2';
import axiosInstance from '@/api/axios';
import {GHGAvertedCard} from './GHGaverted';
import {RecyclingHeader} from './siteheader';
import {CircularityExport} from './export/CircularityExport';
import {describePeriod} from './export/exportUtils';

// Where the WP4 report PDF is served from.
// Put the file in your frontend `public/reports/` folder (or change this URL).
const REPORT_URL = '/reports/PLAEX_Systems_Dashboarding_Research_Report.pdf';

// --- localStorage persistence helpers -------------------------------------
const LS_KEYS = {
    year: 'recyclingDashboard.year',
    bins: 'recyclingDashboard.selectedBins',
    wasteType: 'recyclingDashboard.selectedWasteType',
    period: 'recyclingDashboard.selectedTimePeriod',
    dateRange: 'recyclingDashboard.customDateRange',
    isCustomRange: 'recyclingDashboard.isCustomDateRange',
};

function readLS(key, fallback) {
    if (typeof window === 'undefined') return fallback;
    try {
        const raw = window.localStorage.getItem(key);
        return raw === null ? fallback : JSON.parse(raw);
    } catch {
        return fallback;
    }
}

function writeLS(key, value) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // storage full/disabled — fail silently
    }
}
// ---------------------------------------------------------------------------

export default function RecyclingDashboard() {
    const {t} = useTranslation();

    // Lazy initializers: read from localStorage once on first render instead
    // of hardcoded defaults, so a refresh restores whatever was last picked.
    const [year, setYear] = useState(() => readLS(LS_KEYS.year, new Date().getFullYear()));
    const [selectedBins, setSelectedBins] = useState(() => readLS(LS_KEYS.bins, []));
    const [selectedWasteType, setSelectedWasteType] = useState(() => readLS(LS_KEYS.wasteType, 'organic'));
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(() => readLS(LS_KEYS.period, 'year'));
    const [isFetching, setIsFetching] = useState(false);
    const [apiData, setApiData] = useState(null);
    const [environmentalImpactData, setEnvironmentalImpactData] = useState(null);
    const [availableBins, setAvailableBins] = useState([]);

    // Custom date range states — also restored from localStorage.
    const [customDateRange, setCustomDateRange] = useState(() =>
        readLS(LS_KEYS.dateRange, [{ startDate: null, endDate: null, key: 'selection' }])
    );
    const [isCustomDateRange, setIsCustomDateRange] = useState(() => readLS(LS_KEYS.isCustomRange, false));

    // Persist each piece of state whenever it changes.
    useEffect(() => { writeLS(LS_KEYS.year, year); }, [year]);
    useEffect(() => { writeLS(LS_KEYS.bins, selectedBins); }, [selectedBins]);
    useEffect(() => { writeLS(LS_KEYS.wasteType, selectedWasteType); }, [selectedWasteType]);
    useEffect(() => { writeLS(LS_KEYS.period, selectedTimePeriod); }, [selectedTimePeriod]);
    useEffect(() => { writeLS(LS_KEYS.dateRange, customDateRange); }, [customDateRange]);
    useEffect(() => { writeLS(LS_KEYS.isCustomRange, isCustomDateRange); }, [isCustomDateRange]);

    // Stable serialized key for useEffect dependency — avoids object reference issues
    const customDateRangeKey = useMemo(() => {
        if (!isCustomDateRange) return null;
        const r = customDateRange[0];
        if (!r?.startDate || !r?.endDate) return null;
        const start = r.startDate instanceof Date ? r.startDate.toISOString() : r.startDate;
        const end = r.endDate instanceof Date ? r.endDate.toISOString() : r.endDate;
        return `${start}__${end}`;
    }, [customDateRange, isCustomDateRange]);

    // Keep a live ref of selectedBins so the bin-id fetch effect below never
    // reads a stale closure value — this was the actual bug: with an empty
    // dependency array, `selectedBins.length === 0` always evaluated against
    // the initial-render value, so a restored selection got silently
    // overwritten as soon as the bin_ids call resolved.
    const selectedBinsRef = useRef(selectedBins);
    useEffect(() => { selectedBinsRef.current = selectedBins; }, [selectedBins]);

    // Fetch bin IDs dynamically
    useEffect(() => {
        const fetchBinIds = async () => {
            try {
                const response = await axiosInstance.get("bin_ids");
                const binIds = response.data || [];
                setAvailableBins(binIds);
                // Only fall back to auto-selecting the first bin if there's
                // truly nothing selected (nothing restored from storage and
                // nothing picked yet) — checked against the live ref, not a
                // stale closure value.
                if (binIds.length > 0 && selectedBinsRef.current.length === 0) {
                    setSelectedBins([binIds[0]]);
                }
            } catch (error) {
                console.error("RecyclingDashboard: Error fetching bin IDs:", error);
            }
        };
        fetchBinIds();
    }, []);

    const handleCustomDateRangeChange = (dateRange, isCustom) => {
        setCustomDateRange(dateRange);
        setIsCustomDateRange(isCustom);
        if (isCustom) {
            setSelectedTimePeriod('custom');
        }
    };

    // Build shared request params — single source of truth
    const buildParams = (extraParams = {}) => {
        const params = {
            'bins[]': selectedBins,
            // Viewer's browser timezone, so every card buckets hours/days the same way.
            tz: (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'Europe/Amsterdam',
            ...extraParams
        };
        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
            const r = customDateRange[0];
            params.start_date = r.startDate instanceof Date
                ? r.startDate.toISOString()
                : r.startDate;
            params.end_date = r.endDate instanceof Date
                ? r.endDate.toISOString()
                : r.endDate;
        } else {
            params.period = selectedTimePeriod;
            if (selectedTimePeriod === 'year') {
                params.year = year;
            }
        }
        return params;
    };

    const fetchRecyclingData = async () => {
        if (selectedBins.length === 0) return;
        setIsFetching(true);
        try {
            const response = await axiosInstance.get('recycling_rate', { params: buildParams() });
            setApiData(response.data);
        } catch (error) {
            console.error("Error fetching recycling data:", error);
            setApiData({
                recycling_rate: 0,
                historical_data: [],
                period: selectedTimePeriod,
                year
            });
        } finally {
            setIsFetching(false);
        }
    };

    const fetchEnvironmentalImpactData = async () => {
        if (selectedBins.length === 0) return;
        try {
            const response = await axiosInstance.get('enviromental_impact', { params: buildParams() });
            setEnvironmentalImpactData(response.data);
        } catch (error) {
            console.error("Error fetching environmental impact data:", error);
            setEnvironmentalImpactData({
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            });
        }
    };

    // Re-fetch whenever bins, period, year, or custom date range changes.
    // customDateRangeKey is a stable string derived from the date range, so it
    // correctly triggers the effect when the user picks a new date range.
    //
    // NOTE: waste-diverted data is intentionally NOT fetched here — the
    // WasteDiverted component below fetches its own data internally (hitting
    // the correct `waste_diverted/` endpoint). A previous version of this
    // effect also fetched from a `waste-diverted` (hyphenated) endpoint that
    // doesn't exist in urls.py, silently failed every load, and the result
    // was never consumed by anything. Removed.
    useEffect(() => {
        fetchRecyclingData();
        fetchEnvironmentalImpactData();
    }, [year, selectedBins, selectedTimePeriod, customDateRangeKey]);

    const dashboardData = useMemo(() => {
        const defaultData = {
            recycling_rate: 0,
            historical_data: [],
            period: selectedTimePeriod,
            year
        };
        const data = apiData || defaultData;
        const recyclingRate = data.recycling_rate || 0;
        return {
            treesEquivalent: (recyclingRate * 0.5).toFixed(1),
            recyclabilityScore: 0,
            wasteDiverted: {
                amount: (recyclingRate * 100).toFixed(0),
                percentage: recyclingRate.toFixed(1)
            },
            recyclingRate,
            historicalData: data.historical_data || [],
            period: data.period,
            year: data.year,
            // Forwarded so RecyclingChart can show the same date-range label
            // as the other cards (Environmental Impact, Waste Diverted, etc.).
            // Falls back to the calendar's own dates when the backend doesn't
            // return start_date/end_date (e.g. short hourly custom ranges).
            startDate: data.start_date || (isCustomDateRange ? customDateRange[0]?.startDate : undefined),
            endDate: data.end_date || (isCustomDateRange ? customDateRange[0]?.endDate : undefined),
            environmentalImpact: environmentalImpactData || {
                total_co2_without_garby: 0,
                total_co2_with_garby: 0,
                co2_savings: 0,
                equivalents: {},
                period_start: null,
                period_end: null
            }
        };
    }, [apiData, environmentalImpactData, year, selectedTimePeriod, isCustomDateRange, customDateRange]);

    // NOTE: the dashboard no longer gates the whole grid behind a single
    // "Loading data..." block. selectedBins.length === 0 is momentary (bin
    // IDs load right after mount and auto-select the first one), and each
    // card already fetches its own data and knows how to show itself while
    // waiting — RecyclingChart takes an explicit `loading` prop since its
    // data comes from this parent; the rest (TreesEquivalent,
    // RecyclabilityScore, WasteDiverted, WasteImpactCard, CO2SavingsChart)
    // manage their own internal loading state and render their own
    // skeletons. This keeps the header/filters interactive immediately and
    // lets each card settle independently instead of the whole page
    // blanking out on every re-fetch.
    // Header written at the top of the page CSV (period + bins, times in the viewer's timezone).
    const exportMeta = {
        period: describePeriod({
            period: selectedTimePeriod, year, customDateRange, isCustomDateRange,
        }),
        bins: selectedBins,
    };

    return (
        <CircularityExport meta={exportMeta}>
            <RecyclingHeader
                selectedBins={selectedBins}
                setSelectedBins={setSelectedBins}
                selectedWasteType={selectedWasteType}
                setSelectedWasteType={setSelectedWasteType}
                selectedTimePeriod={selectedTimePeriod}
                setSelectedTimePeriod={setSelectedTimePeriod}
                year={year}
                setYear={setYear}
                allBins={availableBins}
                customDateRange={customDateRange}
                onCustomDateRangeChange={handleCustomDateRangeChange}
                isCustomDateRange={isCustomDateRange}
            />
            <div className="w-full max-w-[1920px] sm:px-4 m-auto sm:pt-0">
                <div className="space-y-6 pb-8 pt-4">
                    {/* First Row - 4 charts */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-4 sm:pt-0 items-start">
                        <div className="min-h-[200px]">
                            <RecyclingChart
                                loading={isFetching || selectedBins.length === 0}
                                isCustomRange={isCustomDateRange}
                                bins={selectedBins}
                                data={{
                                    recycling_rate: dashboardData.recyclingRate,
                                    historical_data: dashboardData.historicalData,
                                    period: dashboardData.period,
                                    year: dashboardData.year,
                                    start_date: dashboardData.startDate,
                                    end_date: dashboardData.endDate
                                }}/>
                        </div>

                        <div className="min-h-[200px]">
                            <TreesEquivalent
                                selectedBins={selectedBins}
                                selectedPeriod={selectedTimePeriod}
                                selectedYear={year}
                                customDateRange={isCustomDateRange ? customDateRange : null}
                            />
                        </div>

                        <div className="min-h-[200px]">
                            <RecyclabilityScore
                                year={year}
                                bins={selectedBins}
                                period={selectedTimePeriod}
                                customDateRange={isCustomDateRange ? customDateRange : null}
                            />
                        </div>

                        <div className="min-h-[200px] md:col-span-1">
                            <WasteDiverted
                                selectedBins={selectedBins}
                                selectedTimePeriod={selectedTimePeriod}
                                year={year}
                                customDateRange={isCustomDateRange ? customDateRange : null}
                            />
                        </div>
                    </div>

                    {/* Second Row - Waste Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 sm:gap-4 sm:pt-0 items-start">
                        <div className="w-full">
                            <WasteImpactCard
                                bins={selectedBins}
                                period={selectedTimePeriod}
                                year={year}
                                customDateRange={isCustomDateRange ? customDateRange : null}
                            />
                        </div>

                        <div className="w-full">
                            <CO2SavingsChart
                                id={selectedBins[0] || ''}
                                bins={selectedBins}
                                wasteitem={selectedWasteType}
                                dayvalue={selectedTimePeriod === 'year' ? year : selectedTimePeriod}
                                selectedButton_={selectedTimePeriod === 'custom' ? 'custom' : 'day'}
                                datevalue={isCustomDateRange ? customDateRange : [{startDate: null, endDate: null, key: 'selection'}]}
                                co2Data={dashboardData.environmentalImpact}
                            />
                        </div>
                    </div>

                    {/* Calculation source */}
                    <div className="pb-14">
                        <div className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5">
                            <p className="text-xs text-gray-600">
                                {t('calculationsBasedOnReport')}
                            </p>
                            <a
                                href={REPORT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 text-xs font-medium text-gray-800 underline hover:text-gray-900"
                            >
                                {t('openPdf')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </CircularityExport>
    );
}